import React, { useEffect, useMemo, useState } from "react";
import { Link } from "react-router-dom";
import { api } from "../lib/api";
import { useAuth } from "../lib/auth";
import { normalizeActivityStats, formatStatNumber } from "../lib/activityStats";
import { usePersonalization } from "../lib/personalization";
import { useContent } from "../lib/content";
import { isPageAvailable, normalizeStage } from "../lib/lifecycle";
import { ArrowRight, CheckCircle2, FileText, Sparkles, Route, ClipboardList, MessageCircle, Calendar, Star, X, FileSignature, FolderArchive, TrendingUp } from "lucide-react";
import { HOA_CATEGORIES } from "./Documents";

/** Lightweight nudge on the dashboard when the agent has turned on HOA
 *  uploads for this seller but at least one of the 4 categories is still
 *  empty. Disappears entirely once all 4 categories have a file — we don't
 *  want a "complete" state nag-card cluttering the dashboard forever.
 */
function HoaReminderCard({ hasHoa, docs }) {
  if (!hasHoa) return null;
  const missing = HOA_CATEGORIES.filter((c) => !docs.some((d) => d.category === c.id));
  if (missing.length === 0) return null;
  return (
    <Link
      to="/documents"
      data-testid="hoa-reminder-card"
      style={{
        display: "flex", alignItems: "center", justifyContent: "space-between", gap: 16,
        padding: "20px 24px", marginTop: 32,
        background: "linear-gradient(135deg, #FAF6EE 0%, #F4ECD8 100%)",
        border: "1px solid #E8D9B5", borderRadius: 14,
        textDecoration: "none", color: "var(--text)",
        boxShadow: "0 4px 14px rgba(28,25,23,0.04)",
      }}
    >
      <div style={{ display: "flex", alignItems: "center", gap: 14, minWidth: 0 }}>
        <div style={{
          width: 40, height: 40, borderRadius: 10, flexShrink: 0,
          background: "rgba(43,76,63,0.12)", color: "var(--primary)",
          display: "inline-flex", alignItems: "center", justifyContent: "center",
        }}>
          <FolderArchive size={20} />
        </div>
        <div style={{ minWidth: 0 }}>
          <div className="overline" style={{ color: "var(--secondary)" }}>HOA packet</div>
          <div style={{ fontSize: 16, fontWeight: 500, marginTop: 2 }}>
            {missing.length === HOA_CATEGORIES.length
              ? "Upload your HOA documents"
              : `${missing.length} HOA section${missing.length === 1 ? "" : "s"} still need${missing.length === 1 ? "s" : ""} a file`}
          </div>
          <div style={{ fontSize: 13, color: "var(--text-muted)", marginTop: 4 }}>
            Missing: {missing.map((m) => m.label).join(" · ")}
          </div>
        </div>
      </div>
      <span style={{ display: "inline-flex", alignItems: "center", gap: 6, color: "var(--primary)", fontWeight: 500, fontSize: 14, whiteSpace: "nowrap" }}>
        Upload <ArrowRight size={16} />
      </span>
    </Link>
  );
}

/**
 * Picks the single most important "do this next" action for the seller and
 * returns a nicely-styled hero card. The decision priority:
 *   1. Listing Input Form not submitted → that, every time (it's the keystone form)
 *   2. Home Story not submitted → that
 *   3. Showing Preferences not submitted → that
 *   4. Prep checklist < 80% done → finish prep
 *   5. Open agent reply waiting on the prep guide → read it
 *   6. Everything done → encouraging "you're listing-ready" state
 */
function NextStepCard({ submittedFormKeys, prepDone, prepTotal, prepPct, sellerTasks, agentFirstName, live = false }) {
  const has = (k) => submittedFormKeys.has(k);

  const FORMS = [
    { key: "listing_input", label: "Listing Input Form", desc: `Property details + inclusions ${agentFirstName} needs to start your listing`, liveDesc: `Property details + inclusions ${agentFirstName} still needs for your listing`, to: "/forms" },
    { key: "home_story", label: "Your Home's Story", desc: `A few quick questions so ${agentFirstName} can market your home well`, to: "/forms" },
    { key: "showing_preferences", label: "Showing Preferences", desc: "How showings should be handled for your home", to: "/forms" },
  ];

  // Once the home is on the market every line below is written for a
  // seller who is still getting ready, and all of it is wrong: "needs to
  // start your listing" when it started, "keep your prep moving" when
  // prep is over, "you're listing-ready" when it is listed. The live
  // card above owns the page from here. Only two things still deserve to
  // interrupt it - a message from the agent, and paperwork that
  // genuinely has not come back. Otherwise this card steps aside.
  if (live) {
    const reply = (sellerTasks || []).find((t) => t.agent_reply && !t.agent_reply_read);
    if (reply) {
      return (
        <NextStepHero
          icon={<MessageCircle size={20} />}
          tagline="A reply from your agent"
          title="You have a new message"
          subtitle={`${agentFirstName} replied to one of your questions - open the guide to read it.`}
          to="/prep-guide"
          cta="Read the reply"
          testid="next-step-reply"
        />
      );
    }
    for (const f of FORMS) {
      if (!has(f.key)) {
        return (
          <NextStepHero
            icon={<FileText size={20} />}
            tagline="Still needed"
            title={f.label}
            subtitle={f.liveDesc || f.desc}
            to={f.to}
            cta="Open form"
            testid={`next-step-${f.key}`}
          />
        );
      }
    }
    return null;
  }

  // Step 1–3: outstanding forms
  for (const f of FORMS) {
    if (!has(f.key)) {
      return (
        <NextStepHero
          icon={<FileText size={20} />}
          tagline="Your next step"
          title={f.label}
          subtitle={f.desc}
          to={f.to}
          cta="Open form"
          testid={`next-step-${f.key}`}
        />
      );
    }
  }

  // Step 4: prep work
  if (prepTotal > 0 && prepPct < 80) {
    return (
      <NextStepHero
        icon={<ClipboardList size={20} />}
        tagline="Your next step"
        title="Keep your prep moving"
        subtitle={`You've checked off ${prepDone} of ${prepTotal} prep items — let's keep the momentum going.`}
        to="/prep-guide"
        cta="Open Prep Guide"
        testid="next-step-prep"
      />
    );
  }

  // Step 5: unread agent reply on a prep task
  const unread = (sellerTasks || []).find((t) => t.agent_reply && !t.agent_reply_read);
  if (unread) {
    return (
      <NextStepHero
        icon={<MessageCircle size={20} />}
        tagline="A reply from your agent"
        title="You have a new message on your prep guide"
        subtitle={`${agentFirstName} replied to one of your prep questions — open the guide to read it.`}
        to="/prep-guide"
        cta="Read the reply"
        testid="next-step-reply"
      />
    );
  }

  // Step 6: all caught up
  return (
    <NextStepHero
      icon={<CheckCircle2 size={20} />}
      tagline="You're listing-ready"
      title="Beautiful work — you've finished everything on your end"
      subtitle={`${agentFirstName} takes it from here. Watch your dates page for the next milestone.`}
      to="/dates"
      cta="See your timeline"
      testid="next-step-done"
      muted
    />
  );
}

const fmtFeedbackDay = (iso) => {
  if (!iso) return "";
  const d = new Date(`${iso}T00:00:00`);
  if (Number.isNaN(d.getTime())) return iso;
  return d.toLocaleDateString(undefined, { month: "short", day: "numeric", year: "numeric" });
};

function LiveListingCard() {
  const [data, setData] = useState({ feedback: [], showings: [], loaded: false });
  const { p } = usePersonalization();

  useEffect(() => {
    let alive = true;
    Promise.all([
      api.get("/me/feedback").catch(() => ({ data: { feedback: [] } })),
      api.get("/me/showings").catch(() => ({ data: { showings: [] } })),
    ]).then(([fb, sh]) => {
      if (!alive) return;
      setData({ feedback: fb.data.feedback || [], showings: sh.data.showings || [], loaded: true });
    });
    return () => { alive = false; };
  }, []);

  const stats = useMemo(() => normalizeActivityStats(p.activity_stats), [p.activity_stats]);
  const today = new Date().toISOString().slice(0, 10);

  const nextVisit = useMemo(() => {
    return (data.showings || [])
      .filter((s) => s.status !== "cancelled" && s.date >= today)
      .sort((a, b) => `${a.date}${a.start_time}`.localeCompare(`${b.date}${b.start_time}`))[0];
  }, [data.showings, today]);

  const heldCount = (data.showings || []).filter((s) => s.status !== "cancelled" && s.date < today).length;
  const latest = (data.feedback || [])[0];

  const tiles = [
    { key: "listing_views", label: "Listing views" },
    { key: "social_views", label: "Social views" },
  ].filter((t) => stats[t.key]?.enabled !== false && stats[t.key]?.value);

  if (!data.loaded) return null;
  const nothingYet = !tiles.length && !heldCount && !nextVisit && !latest;

  const fmtVisit = (s) => {
    const d = new Date(`${s.date}T00:00:00`);
    const day = Number.isNaN(d.getTime())
      ? s.date
      : d.toLocaleDateString(undefined, { weekday: "long", month: "short", day: "numeric" });
    const [hh, mm] = String(s.start_time || "").split(":");
    const hr = parseInt(hh, 10);
    const time = Number.isNaN(hr) ? "" : ` at ${((hr + 11) % 12) + 1}:${mm} ${hr >= 12 ? "PM" : "AM"}`;
    return `${day}${time}`;
  };

  return (
    <div
      data-testid="live-listing-card"
      style={{
        marginTop: 32, background: "#fff", border: "1px solid var(--border)",
        borderLeft: "3px solid var(--primary)", borderRadius: 12, overflow: "hidden",
      }}
    >
      <div style={{ padding: "20px 24px 0" }}>
        <div style={{ display: "flex", alignItems: "center", gap: 8 }}>
          <span style={{ width: 8, height: 8, borderRadius: "50%", background: "var(--primary)", display: "inline-block" }} />
          <span className="overline" style={{ color: "var(--primary)" }}>Your listing is live</span>
        </div>
        <div className="font-serif" style={{ fontSize: 22, fontWeight: 500, marginTop: 6 }}>
          {nothingYet ? "You're on the market." : "Here's how it's going."}
        </div>
      </div>

      {nothingYet ? (
        <div style={{ padding: "10px 24px 20px", color: "var(--text-muted)", fontSize: 14, lineHeight: 1.6 }}>
          Nothing to report just yet — views, showings and buyer feedback will show up here as they come in.
        </div>
      ) : (
        <>
          <div style={{ display: "flex", flexWrap: "wrap", gap: 28, padding: "16px 24px 4px" }}>
            {tiles.map((t) => (
              <div key={t.key} data-testid={`live-tile-${t.key}`}>
                <div className="font-serif" style={{ fontSize: 26, fontWeight: 500 }}>{formatStatNumber(stats[t.key]?.value)}</div>
                <div style={{ fontSize: 11, letterSpacing: "0.06em", textTransform: "uppercase", color: "var(--text-muted)", marginTop: 2 }}>{t.label}</div>
              </div>
            ))}
            {heldCount > 0 && (
              <div data-testid="live-tile-visits">
                <div className="font-serif" style={{ fontSize: 26, fontWeight: 500 }}>{heldCount}</div>
                <div style={{ fontSize: 11, letterSpacing: "0.06em", textTransform: "uppercase", color: "var(--text-muted)", marginTop: 2 }}>
                  Visit{heldCount === 1 ? "" : "s"} so far
                </div>
              </div>
            )}
          </div>

          {nextVisit && (
            <div style={{ padding: "14px 24px 0", fontSize: 14 }} data-testid="live-next-visit">
              <span style={{ color: "var(--text-muted)" }}>
                Next {nextVisit.kind === "open_house" ? "open house" : "showing"}:{" "}
              </span>
              <b style={{ fontWeight: 500 }}>{fmtVisit(nextVisit)}</b>
            </div>
          )}

          {latest && (
            <div style={{ margin: "16px 24px 0", padding: "14px 16px", background: "var(--surface-alt)", borderRadius: 10 }} data-testid="live-latest-feedback">
              <div style={{ fontSize: 11, letterSpacing: "0.06em", textTransform: "uppercase", color: "var(--text-muted)", fontWeight: 600 }}>
                Latest buyer feedback
              </div>
              <div style={{ marginTop: 6, lineHeight: 1.6, fontSize: 14 }}>
                {latest.body.length > 180 ? `${latest.body.slice(0, 180).trim()}…` : latest.body}
              </div>
            </div>
          )}
        </>
      )}

      <Link
        to="/activity"
        data-testid="live-see-all"
        style={{
          display: "flex", alignItems: "center", justifyContent: "space-between",
          padding: "14px 24px", marginTop: 16, borderTop: "1px solid var(--border)",
          textDecoration: "none", color: "var(--primary)", fontWeight: 500, fontSize: 14,
        }}
      >
        See all your home activity <ArrowRight size={16} />
      </Link>
    </div>
  );
}

function NextStepHero({ icon, tagline, title, subtitle, to, cta, testid, muted = false }) {
  return (
    <Link
      to={to}
      data-testid={testid || "next-step-card"}
      className="card"
      style={{
        display: "block",
        marginTop: 28,
        padding: 24,
        background: muted ? "var(--surface-alt)" : "linear-gradient(135deg, var(--primary) 0%, #1F3A2F 100%)",
        color: muted ? "var(--text)" : "#FBFAF6",
        textDecoration: "none",
        border: muted ? "1px solid var(--border)" : "none",
        transition: "transform 160ms ease",
      }}
      onMouseEnter={(e) => { e.currentTarget.style.transform = "translateY(-2px)"; }}
      onMouseLeave={(e) => { e.currentTarget.style.transform = "translateY(0)"; }}
    >
      <div style={{ display: "flex", gap: 18, alignItems: "flex-start" }}>
        <div style={{
          width: 44, height: 44, borderRadius: 12,
          background: muted ? "var(--primary)" : "rgba(251, 250, 246, 0.18)",
          color: muted ? "#fff" : "#FBFAF6",
          display: "flex", alignItems: "center", justifyContent: "center", flexShrink: 0,
        }}>
          {icon}
        </div>
        <div style={{ flex: 1, minWidth: 0 }}>
          <div className="overline" style={{ color: muted ? "var(--text-muted)" : "rgba(251, 250, 246, 0.7)" }}>{tagline}</div>
          <h2 className="font-serif" style={{ fontSize: 26, fontWeight: 500, margin: "4px 0 6px", letterSpacing: "-0.01em", lineHeight: 1.2 }}>{title}</h2>
          <p style={{ color: muted ? "var(--text-muted)" : "rgba(251, 250, 246, 0.85)", margin: 0, fontSize: 14.5, lineHeight: 1.55 }}>{subtitle}</p>
        </div>
        <div style={{
          display: "inline-flex", alignItems: "center", gap: 6, padding: "10px 16px",
          background: muted ? "var(--primary)" : "#FBFAF6",
          color: muted ? "#fff" : "var(--primary)",
          borderRadius: 9999, fontWeight: 600, fontSize: 14, flexShrink: 0,
          alignSelf: "center",
        }}>
          {cta} <ArrowRight size={14} />
        </div>
      </div>
    </Link>
  );
}

function ProgressRing({ pct = 0, size = 80 }) {
  const r = (size - 10) / 2;
  const c = 2 * Math.PI * r;
  const offset = c - (pct / 100) * c;
  return (
    <svg width={size} height={size} className="progress-ring">
      <circle cx={size / 2} cy={size / 2} r={r} strokeWidth="6" fill="none" className="progress-ring__bg" />
      <circle cx={size / 2} cy={size / 2} r={r} strokeWidth="6" fill="none" strokeLinecap="round" strokeDasharray={c} strokeDashoffset={offset} className="progress-ring__fg" />
    </svg>
  );
}

export default function Dashboard() {
  const { user } = useAuth();
  const { p } = usePersonalization();
  const { content } = useContent();
  const branding = content?.branding || {};
  const agentFirstName = branding.agent_first_name || (branding.agent_name || "your agent").split(" ")[0];
  // Buy-side flag drives what cards render below. Keeps this component
  // side-aware without splitting into two pages that would drift.
  const isBuySide = content?.side === "buy";
  // Agents see every quick link (they're supporting sellers at all stages);
  // sellers see only what applies to theirs.
  const stageAllows = (path) =>
    user?.role === "admin" || isPageAvailable(path, user?.lifecycle_status);
  const [progress, setProgress] = useState([]);
  const [submissions, setSubmissions] = useState([]);
  const [docs, setDocs] = useState([]);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    (async () => {
      try {
        const [p, s, d] = await Promise.all([
          api.get("/progress"),
          api.get("/forms/my-submissions"),
          api.get("/me/documents").catch(() => ({ data: { documents: [] } })),
        ]);
        setProgress(p.data.progress || []);
        setSubmissions(s.data.submissions || []);
        setDocs(d.data.documents || []);
      } finally { setLoading(false); }
    })();
  }, []);

  const prepSections = [
    { key: "repairs", total: (content.prep.repairs || []).length, label: "Repairs" },
    { key: "purges", total: (content.prep.purges || []).length, label: "Purges" },
    { key: "photo_prep", total: (content.prep.photo_prep || []).length, label: "Photo and Showings Prep" },
  ];
  const prepDone = prepSections.reduce((acc, s) => {
    const entry = progress.find((p) => p.section === s.key);
    return acc + (entry?.completed_items?.length || 0);
  }, 0);
  const prepTotal = prepSections.reduce((a, s) => a + s.total, 0);
  const prepPct = prepTotal ? Math.round((prepDone / prepTotal) * 100) : 0;

  // Forms split by listing phase. The pre-listing set drives the "Your forms"
  // stat card before the home goes under contract; the under-contract set
  // (just the Buyer Welcome Info packet today) drives a phase-appropriate
  // version once the deal is in escrow.
  const PRE_LISTING_FORMS = [
    { key: "listing_input", label: "Listing Input Form" },
    { key: "home_story", label: "Your Home's Story" },
    { key: "showing_preferences", label: "Showing Preferences" },
  ];
  const UNDER_CONTRACT_FORMS = [
    { key: "buyer_info", label: "Buyer Welcome Info" },
  ];
  const submittedKeys = new Set(submissions.map((s) => s.form_type));
  const FORM_TYPES = PRE_LISTING_FORMS;
  const formsPct = Math.round((submittedKeys.size / FORM_TYPES.length) * 100);

  // Marketing-screenshot mode: when the URL has `?marketing=1`, hide the
  // pre-listing scaffolding (NextStep hero, prep/forms stats, recent
  // submissions, quick access) so the dashboard becomes a clean
  // greeting + Upcoming Milestones layout for the launch-email hero shot.
  // No production user ever hits this flag — it's purely for the
  // refresh_marketing_dashboard_screenshot.py script.
  const marketingMode = typeof window !== "undefined" && new URLSearchParams(window.location.search).get("marketing") === "1";

  // Once the listing is under contract, the pre-listing scaffolding (prep
  // progress hero, recent form submissions, key-date countdowns, and the
  // "What's ahead" timeline preview) is mostly noise. Sellers should see the
  // contract milestones front-and-center, but we still surface any
  // outstanding under-contract forms (e.g. Buyer Welcome Info) so the seller
  // knows there's still paperwork to file. Quick-access links stay because
  // sellers still occasionally pull up the prep guide / vendors.
  const isUnderContract = !!(p.under_contract && p.under_contract.active);
  const ucFormsPending = UNDER_CONTRACT_FORMS.filter((f) => !submittedKeys.has(f.key));

  return (
    <div className="container-x" style={{ padding: "48px 0 64px" }}>
      <div style={{ display: "flex", alignItems: "center", justifyContent: "space-between", flexWrap: "wrap", gap: 12 }}>
        <div>
          <span className="overline">Your dashboard</span>
          <h1 className="font-serif" style={{ fontSize: "clamp(2rem, 4vw, 3rem)", fontWeight: 500, margin: "8px 0 0", letterSpacing: "-0.01em" }}>
            Hello, {user?.name?.split(" ")[0] || "there"}.
          </h1>
          <p style={{ color: "var(--text-muted)", marginTop: 8 }}>
            {isBuySide
              ? "Let\u2019s find you a home. Every step of the process, kept clear."
              : (user?.property_address || "Add your property address from your profile to personalize this hub.")
            }
          </p>
        </div>
      </div>

      {/* Under-contract, HOA, and next-step cards are all seller-side
          concepts — buyers don't have HOA docs, listing input forms, or
          a "buyer welcome info" packet. Hide the whole seller-only stack
          when the active side is buy; leave everything else (timeline,
          milestones, quick links) alive because those are already
          side-adapted via content.timeline / content.date_fields. */}
      {!isBuySide && <UnderContractCard p={p} pinned />}

      {/* Listing just went live — surface the numbers the seller actually cares
          about right here, rather than making them find /activity.

          Use normalizeStage rather than a raw === "active": a seller whose
          stage was never set is treated as active everywhere else (nav,
          routing, StageRoute), and a strict compare here made the dashboard
          disagree with the page it links to. */}
      {!isBuySide && normalizeStage(user?.lifecycle_status) === "active" && <LiveListingCard />}

        {/* Buyer feedback shows only while the listing is active, and only
            inside the card above. It used to also render on its own at every
            other stage, on the reasoning that what buyers said outlives the
            view counts. In practice a seller who hadn't listed yet was being
            shown notes from showings that, to them, hadn't happened -- so the
            agent asked for it to retire with the rest of Home Activity. */}

      {/* Pending under-contract forms — nudges the seller to file the Buyer
          Welcome Info packet (trash day, garage code, alarm code, etc.) so the
          agent has a branded sheet to hand to the buyer at closing. Disappears
          once submitted. */}
      {isUnderContract && ucFormsPending.length > 0 && (
        <div style={{ marginTop: 32 }} data-testid="uc-pending-forms">
          {ucFormsPending.map((f) => (
            <Link
              key={f.key}
              to="/forms"
              data-testid={`uc-pending-form-${f.key}`}
              style={{
                display: "flex", alignItems: "center", justifyContent: "space-between", gap: 16,
                padding: "20px 24px", marginTop: 12,
                background: "#fff", border: "1px solid var(--border)",
                borderLeft: "3px solid var(--secondary)",
                borderRadius: 12, textDecoration: "none", color: "inherit",
              }}
            >
              <div style={{ display: "flex", alignItems: "center", gap: 14 }}>
                <span style={{
                  width: 40, height: 40, borderRadius: 10, flexShrink: 0,
                  background: "var(--surface-alt)", color: "var(--secondary)",
                  display: "inline-flex", alignItems: "center", justifyContent: "center",
                }}><FileSignature size={18} /></span>
                <div>
                  <div className="overline" style={{ color: "var(--secondary)" }}>One more form</div>
                  <div className="font-serif" style={{ fontSize: 20, fontWeight: 500, marginTop: 2 }}>{f.label}</div>
                  <div style={{ color: "var(--text-muted)", fontSize: 13, marginTop: 2 }}>
                    Trash day, garage code, alarm code, neighborhood tips — for the new owner.
                  </div>
                </div>
              </div>
              <span style={{ display: "inline-flex", alignItems: "center", gap: 6, color: "var(--primary)", fontWeight: 500, fontSize: 14, whiteSpace: "nowrap" }}>
                Open form <ArrowRight size={16} />
              </span>
            </Link>
          ))}
        </div>
      )}

      {/* HOA packet reminder — visible when admin has flipped on `has_hoa`
          for this seller AND at least one HOA section still has zero files.
          Disappears entirely once all 4 categories have at least one doc. */}
      {!marketingMode && !isBuySide && (
        <HoaReminderCard hasHoa={!!p.has_hoa} docs={docs} />
      )}

      {/* The single most important "do this next" card. Lives at the top so the seller
          always knows their one true next action — pulled from forms / prep / messages.
          Hidden once under contract since prep/forms are no longer the active focus. */}
      {!isUnderContract && !marketingMode && !isBuySide && (
        <NextStepCard
            live={normalizeStage(user?.lifecycle_status) === "active"}
          submittedFormKeys={submittedKeys}
          prepDone={prepDone}
          prepTotal={prepTotal}
          prepPct={prepPct}
          sellerTasks={p.seller_tasks}
          agentFirstName={agentFirstName}
        />
      )}

      {/* Agent Note */}
      {p.agent_note && (
        <div className="card" style={{ padding: 24, marginTop: 32, background: "var(--surface-alt)", border: "1px solid var(--border)" }} data-testid="agent-note-card">
          <div style={{ display: "flex", gap: 14, alignItems: "flex-start" }}>
            <div style={{ width: 40, height: 40, borderRadius: 10, background: "var(--primary)", color: "#fff", display: "flex", alignItems: "center", justifyContent: "center", flexShrink: 0 }}>
              <MessageCircle size={18} />
            </div>
            <div>
              <div className="overline">A note from your agent</div>
              <p style={{ margin: "6px 0 0", color: "var(--text)", lineHeight: 1.55, whiteSpace: "pre-wrap" }}>{p.agent_note}</p>
            </div>
          </div>
        </div>
      )}

      {/* Post-closing referral moment */}
      <ReferralCard closingDate={p.key_dates?.closing} referrals={content.referrals} userId={user?.id} />

      {/* Unread agent replies — small nudge to the prep guide */}
      <UnreadRepliesCard sellerTasks={p.seller_tasks} />

      {/* Top stat cards — pre-listing metrics, hidden during the under-contract phase.
          Kept in marketingMode so the launch-email hero shot shows real product surface
          area (prep progress + forms progress + up-next) rather than just the greeting. */}
      {!isUnderContract && !isBuySide && (
      <div style={{ display: "grid", gridTemplateColumns: "repeat(auto-fit, minmax(260px, 1fr))", gap: 20, marginTop: 32 }}>
        <div className="card" style={{ padding: 28, display: "flex", gap: 20, alignItems: "center" }} data-testid="prep-progress-card">
          <div style={{ position: "relative", width: 80, height: 80 }}>
            <ProgressRing pct={prepPct} />
            <div style={{ position: "absolute", inset: 0, display: "flex", alignItems: "center", justifyContent: "center", fontWeight: 600 }}>{prepPct}%</div>
          </div>
          <div>
            <div className="overline">Prep Progress</div>
            <div className="font-serif" style={{ fontSize: 24 }}>{prepDone} / {prepTotal} tasks</div>
            <Link to="/prep-guide" style={{ color: "var(--primary)", fontSize: 14, fontWeight: 500, display: "inline-flex", alignItems: "center", gap: 4, marginTop: 6 }} data-testid="goto-prep-link">
              Continue <ArrowRight size={14} />
            </Link>
          </div>
        </div>

        <div className="card" style={{ padding: 28, display: "flex", gap: 20, alignItems: "center" }} data-testid="forms-progress-card">
          <div style={{ position: "relative", width: 80, height: 80 }}>
            <ProgressRing pct={formsPct} />
            <div style={{ position: "absolute", inset: 0, display: "flex", alignItems: "center", justifyContent: "center", fontWeight: 600 }}>{formsPct}%</div>
          </div>
          <div>
            <div className="overline">Forms</div>
            <div className="font-serif" style={{ fontSize: 24 }}>{submittedKeys.size} / {FORM_TYPES.length} submitted</div>
            <Link to="/forms" style={{ color: "var(--primary)", fontSize: 14, fontWeight: 500, display: "inline-flex", alignItems: "center", gap: 4, marginTop: 6 }} data-testid="goto-forms-link">
              Continue <ArrowRight size={14} />
            </Link>
          </div>
        </div>

        <div className="card" style={{ padding: 28 }} data-testid="next-step-card">
          <div className="overline">Up next</div>
          <div className="font-serif" style={{ fontSize: 24, marginTop: 6 }}>
            {prepPct < 100 ? "Finish your prep checklist" : submittedKeys.size < FORM_TYPES.length ? "Complete your forms" : "Ready for launch"}
          </div>
          <p style={{ color: "var(--text-muted)", fontSize: 14, marginTop: 8 }}>
            {prepPct < 100 ? "Small tasks make a big difference in photos and offers." : submittedKeys.size < FORM_TYPES.length ? "Submit remaining forms so your agent has everything." : "Your agent will reach out with next steps."}
          </p>
        </div>
      </div>
      )}

      {/* Quick links — kept in marketingMode so the email hero shows the
          breadth of tools (Prep, Process, Forms, Vendors) at a glance. */}
      <div style={{ marginTop: 48 }}>
        <span className="overline">Quick access</span>
        <div style={{ display: "grid", gridTemplateColumns: "repeat(auto-fit, minmax(240px, 1fr))", gap: 16, marginTop: 16 }}>
          {isBuySide ? (
            <>
              <QuickLink to="/process" icon={<Route size={18} />} title="Buying Process" desc="What happens at each stage" testid="quick-process" />
              <QuickLink to="/important-dates" icon={<ClipboardList size={18} />} title="Important Dates" desc="Inspection, appraisal, closing" testid="quick-dates" />
              <QuickLink to="/vendors" icon={<FileText size={18} />} title="Team" desc="Your agent, lender, inspector" testid="quick-vendors" />
              <QuickLink to="/messages" icon={<Sparkles size={18} />} title="Messages" desc={`Ask ${agentFirstName} anything`} testid="quick-messages" />
            </>
          ) : (
            <>
              {/* Same stage rules as the nav — a quick link to a page the
                  seller can't open yet would just land on the gate screen. */}
              {stageAllows("/prep-guide") && <QuickLink to="/prep-guide" icon={<Sparkles size={18} />} title="Prep Guide" desc="Room-by-room checklists" testid="quick-prep" />}
              <QuickLink to="/process" icon={<Route size={18} />} title="Selling Process" desc="What happens at each stage" testid="quick-process" />
              {stageAllows("/activity") && <QuickLink to="/activity" icon={<TrendingUp size={18} />} title="Home Activity" desc="Views, showings, and interest" testid="quick-activity" />}
              {stageAllows("/forms") && <QuickLink to="/forms" icon={<ClipboardList size={18} />} title="Forms" desc="Submit paperwork online" testid="quick-forms" />}
              <QuickLink to="/vendors" icon={<FileText size={18} />} title="Vendors" desc="Trusted local pros" testid="quick-vendors" />
            </>
          )}
        </div>
      </div>

      {/* Recent submissions — seller-only. Buyer-side document flow is
          Phase 2; hide until we ship it. */}
      {!isUnderContract && !marketingMode && !isBuySide && (
      <div style={{ marginTop: 48 }}>
        <span className="overline">Recent submissions</span>
        <div className="card" style={{ padding: 0, marginTop: 16, overflow: "hidden" }}>
          {loading ? (
            <div style={{ padding: 24, color: "var(--text-muted)" }}>Loading…</div>
          ) : submissions.length === 0 ? (
            <div style={{ padding: 24, color: "var(--text-muted)" }} data-testid="no-submissions">
              No submissions yet. Head to <Link to="/forms" style={{ color: "var(--primary)" }}>Forms</Link> to get started.
            </div>
          ) : (
            <table style={{ width: "100%", borderCollapse: "collapse" }} data-testid="submissions-table">
              <thead>
                <tr style={{ background: "var(--surface-alt)" }}>
                  <th style={{ textAlign: "left", padding: "12px 20px", fontWeight: 600, fontSize: 12, letterSpacing: "0.08em", textTransform: "uppercase", color: "var(--text-muted)" }}>Form</th>
                  <th style={{ textAlign: "left", padding: "12px 20px", fontWeight: 600, fontSize: 12, letterSpacing: "0.08em", textTransform: "uppercase", color: "var(--text-muted)" }}>Submitted</th>
                  <th style={{ textAlign: "left", padding: "12px 20px", fontWeight: 600, fontSize: 12, letterSpacing: "0.08em", textTransform: "uppercase", color: "var(--text-muted)" }}>Status</th>
                  <th></th>
                </tr>
              </thead>
              <tbody>
                {submissions.slice(0, 8).map((s) => (
                  <tr key={s.submission_id} style={{ borderTop: "1px solid var(--border)" }}>
                    <td style={{ padding: "14px 20px" }}>{s.form_title}</td>
                    <td style={{ padding: "14px 20px", color: "var(--text-muted)" }}>{new Date(s.created_at).toLocaleString()}</td>
                    <td style={{ padding: "14px 20px" }}>
                      <span className="badge badge-success" data-testid={`sub-status-${s.submission_id}`}>
                        <CheckCircle2 size={12} style={{ marginRight: 4 }} /> Submitted
                      </span>
                    </td>
                    <td style={{ padding: "14px 20px" }}>
                      <a data-testid={`sub-pdf-${s.submission_id}`} href={`${process.env.REACT_APP_BACKEND_URL}/api/forms/submission/${s.submission_id}/pdf`} target="_blank" rel="noreferrer" style={{ color: "var(--primary)", fontWeight: 500 }}>
                        PDF
                      </a>
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
          )}
        </div>
      </div>
      )}

      {/* Key Dates ↔ Timeline toggle (pre-listing only).
          When the agent has populated any key date in the backend we show
          "Upcoming milestones" since those concrete dates are far more useful
          than a generic process preview. Past dates fall out automatically.
          Until the agent fills anything in, we keep the "What's ahead"
          timeline so a brand-new seller still sees the journey overview. */}
      {!isUnderContract && (() => {
        const allFields = [
          ...(content.date_fields || []).filter((f) => !(p.hidden_date_fields || []).includes(f.key)),
          ...(p.custom_date_fields || []),
        ];
        const today = new Date(); today.setHours(0, 0, 0, 0);
        // "Set in the backend" = any non-empty value on a configured field,
        // regardless of whether the date itself is past or future. So once an
        // agent fills a single date the seller graduates from the timeline to
        // the dates view, even if every saved date has since slipped past.
        const hasAnyKeyDateSet = allFields.some((f) => !!p.key_dates?.[f.key]);
        const upcomingFields = allFields.filter((f) => {
          const v = p.key_dates?.[f.key];
          if (!v) return false;
          const d = new Date(v + "T00:00:00");
          return d.getTime() >= today.getTime();
        }).sort((a, b) => {
          // Chronological order — the seller's next milestone should always
          // sit on the left, not whichever the agent configured first in
          // the CMS. Tie-break by time-of-day if two share the same date.
          const da = p.key_dates?.[a.key] || "";
          const db = p.key_dates?.[b.key] || "";
          if (da !== db) return da < db ? -1 : 1;
          const ta = p.key_dates_times?.[a.key] || "";
          const tb = p.key_dates_times?.[b.key] || "";
          return ta < tb ? -1 : ta > tb ? 1 : 0;
        });
        if (hasAnyKeyDateSet) {
          // Dates view (may render empty if every date has passed; we still
          // suppress the timeline below since the agent has clearly graduated
          // out of the "preview the journey" stage).
          if (upcomingFields.length === 0) {
            return (
              <div style={{ marginTop: 48 }} data-testid="key-dates-section">
                <span className="overline">Your key dates</span>
                <h2 className="font-serif" style={{ fontSize: "clamp(1.75rem, 3vw, 2.25rem)", fontWeight: 500, letterSpacing: "-0.01em", marginTop: 8 }}>Upcoming milestones</h2>
                <div className="card" style={{ marginTop: 16, padding: 28, color: "var(--text-muted)", textAlign: "center" }} data-testid="key-dates-empty">
                  All set milestones have passed. Your agent will add new ones as they're scheduled.
                </div>
              </div>
            );
          }
          return (
            <div style={{ marginTop: 48 }} data-testid="key-dates-section">
              <span className="overline">Your key dates</span>
              <h2 className="font-serif" style={{ fontSize: "clamp(1.75rem, 3vw, 2.25rem)", fontWeight: 500, letterSpacing: "-0.01em", marginTop: 8 }}>Upcoming milestones</h2>
              <div style={{ display: "grid", gridTemplateColumns: "repeat(auto-fit, minmax(200px, 1fr))", gap: 16, marginTop: 20 }}>
                {upcomingFields.map((f) => {
                  const d = new Date(p.key_dates[f.key] + "T00:00:00");
                  const now = new Date(); now.setHours(0, 0, 0, 0);
                  const diffDays = Math.round((d - now) / 86400000);
                  const t = p.key_dates_times?.[f.key];
                  return (
                    <div key={f.key} className="card" style={{ padding: 20 }} data-testid={`date-${f.key}`}>
                      <div style={{ display: "flex", alignItems: "center", gap: 8, color: "var(--primary)" }}><Calendar size={14} /><span className="overline" style={{ color: "var(--primary)" }}>{f.label}</span></div>
                      <div className="font-serif" style={{ fontSize: 22, marginTop: 6 }}>{d.toLocaleDateString(undefined, { month: "long", day: "numeric" })}</div>
                      {t && <div style={{ color: "var(--text)", fontSize: 13, marginTop: 4, fontWeight: 500 }} data-testid={`date-time-${f.key}`}>{t}</div>}
                      <div style={{ color: "var(--text-muted)", fontSize: 13, marginTop: 2 }}>
                        {diffDays > 0 ? `in ${diffDays} day${diffDays === 1 ? "" : "s"}` : "today"}
                      </div>
                    </div>
                  );
                })}
              </div>
            </div>
          );
        }
        // Fallback — agent hasn't set any dates yet, so show the journey
        // timeline so a brand-new seller still has helpful context.
        return (
          <div style={{ marginTop: 48 }} data-testid="timeline-fallback">
            <span className="overline">Your journey</span>
            <h2 className="font-serif" style={{ fontSize: "clamp(1.75rem, 3vw, 2.25rem)", fontWeight: 500, letterSpacing: "-0.01em", marginTop: 8 }}>What's ahead</h2>
            <div style={{ display: "grid", gridTemplateColumns: "repeat(auto-fit, minmax(220px, 1fr))", gap: 16, marginTop: 20 }}>
              {(content.timeline || []).slice(0, 4).map((s, i) => (
                <div key={s.id} className="card" style={{ padding: 20 }} data-testid={`timeline-preview-${s.id}`}>
                  <div className="overline">Stage {String(i + 1).padStart(2, "0")}</div>
                  <div className="font-serif" style={{ fontSize: 20, marginTop: 4 }}>{s.title}</div>
                  <p style={{ color: "var(--text-muted)", fontSize: 14, marginTop: 6 }}>{s.blurb}</p>
                </div>
              ))}
            </div>
            <div style={{ marginTop: 16 }}>
              <Link to="/process" style={{ color: "var(--primary)", fontWeight: 500 }} data-testid="view-full-timeline-link">View full timeline →</Link>
            </div>
          </div>
        );
      })()}
    </div>
  );
}

function QuickLink({ to, icon, title, desc, testid }) {
  return (
    <Link to={to} className="card card-hover" data-testid={testid} style={{ padding: 20, textDecoration: "none", color: "inherit", display: "flex", gap: 14 }}>
      <div style={{ width: 40, height: 40, borderRadius: 10, background: "var(--surface-alt)", color: "var(--primary)", display: "flex", alignItems: "center", justifyContent: "center", flexShrink: 0 }}>{icon}</div>
      <div>
        <div style={{ fontWeight: 600 }}>{title}</div>
        <div style={{ color: "var(--text-muted)", fontSize: 14 }}>{desc}</div>
      </div>
    </Link>
  );
}

function UnreadRepliesCard({ sellerTasks }) {
  const { content } = useContent();
  const cb = content?.branding || {};
  const agentFirst = cb.agent_first_name || (cb.agent_name || "").trim().split(" ")[0] || "your agent";
  const unread = (sellerTasks || []).filter(
    (t) => t.agent_reply && !t.agent_reply.seen
  );
  if (unread.length === 0) return null;
  const preview = unread[0];
  const labelCount = unread.length === 1 ? `1 new reply from ${agentFirst}` : `${unread.length} new replies from ${agentFirst}`;
  return (
    <Link
      to="/prep-guide"
      data-testid="unread-replies-card"
      aria-label={`${labelCount}, opens Prep Guide`}
      className="card card-hover"
      style={{
        display: "flex",
        gap: 14,
        alignItems: "flex-start",
        padding: 20,
        marginTop: 20,
        textDecoration: "none",
        color: "inherit",
        borderLeft: "3px solid var(--primary)",
      }}
    >
      <div style={{ width: 40, height: 40, borderRadius: 10, background: "var(--surface-alt)", color: "var(--primary)", display: "flex", alignItems: "center", justifyContent: "center", flexShrink: 0 }}>
        <MessageCircle size={18} />
      </div>
      <div style={{ flex: 1 }}>
        <div style={{ display: "flex", alignItems: "center", gap: 8, flexWrap: "wrap" }}>
          <span className="overline" style={{ color: "var(--primary)" }}>
            {unread.length === 1 ? `New reply from ${agentFirst}` : `${unread.length} new replies from ${agentFirst}`}
          </span>
        </div>
        <div style={{ marginTop: 4, fontWeight: 500 }}>
          on "{preview.title}"
        </div>
        {preview.agent_reply?.body && (
          <div style={{ marginTop: 4, color: "var(--text-muted)", fontSize: 14, lineHeight: 1.4 }}>
            {preview.agent_reply.body.length > 140
              ? preview.agent_reply.body.slice(0, 140).trim() + "…"
              : preview.agent_reply.body}
          </div>
        )}
        <div style={{ marginTop: 8, color: "var(--primary)", fontSize: 13, fontWeight: 500, display: "inline-flex", alignItems: "center", gap: 4 }}>
          View in Prep Guide <ArrowRight size={14} />
        </div>
      </div>
    </Link>
  );
}

function ReferralCard({ closingDate, referrals, userId }) {
  const r = referrals || {};
  const dismissKey = `sellerhub_referral_dismissed_${userId || "anon"}`;
  const [dismissed, setDismissed] = useState(() => {
    try { return localStorage.getItem(dismissKey) === "1"; } catch { return false; }
  });

  if (!r.enabled) return null;
  if (!closingDate) return null;
  if (dismissed) return null;
  if (!r.google_url && !r.zillow_url) return null;

  const d = new Date(String(closingDate).slice(0, 10) + "T00:00:00");
  if (isNaN(d.getTime())) return null;
  const now = new Date(); now.setHours(0, 0, 0, 0);
  if (d > now) return null; // closing hasn't happened yet

  const dismiss = () => {
    try { localStorage.setItem(dismissKey, "1"); } catch {}
    setDismissed(true);
  };

  return (
    <div
      className="card"
      data-testid="referral-card"
      style={{
        padding: 28,
        marginTop: 32,
        position: "relative",
        overflow: "hidden",
        background: "linear-gradient(135deg, #2F4A3A 0%, #3C6150 100%)",
        color: "#F5F1E8",
        border: "none",
      }}
    >
      <button
        data-testid="referral-dismiss-btn"
        onClick={dismiss}
        aria-label="Dismiss"
        style={{ position: "absolute", top: 14, right: 14, background: "rgba(255,255,255,0.12)", color: "#F5F1E8", border: "none", borderRadius: 8, padding: 6, cursor: "pointer", display: "inline-flex" }}
      >
        <X size={14} />
      </button>

      <div style={{ display: "flex", gap: 16, alignItems: "flex-start", flexWrap: "wrap" }}>
        <div style={{ width: 44, height: 44, borderRadius: 12, background: "rgba(255,255,255,0.15)", display: "flex", alignItems: "center", justifyContent: "center", flexShrink: 0 }}>
          <Star size={20} fill="#F5F1E8" strokeWidth={0} />
        </div>
        <div style={{ flex: 1, minWidth: 240 }}>
          <div className="overline" style={{ color: "rgba(245,241,232,0.7)" }}>Congratulations on closing</div>
          <h2 className="font-serif" style={{ fontSize: "clamp(1.5rem, 2.4vw, 1.9rem)", fontWeight: 500, margin: "6px 0 8px", letterSpacing: "-0.01em" }}>
            {r.headline || "Thank you — truly."}
          </h2>
          <p style={{ margin: 0, lineHeight: 1.55, color: "rgba(245,241,232,0.88)", fontSize: 15 }}>
            {r.message}
          </p>

          <div style={{ display: "flex", gap: 10, marginTop: 20, flexWrap: "wrap" }}>
            {r.google_url && (
              <a
                data-testid="referral-google-btn"
                href={r.google_url}
                target="_blank"
                rel="noopener noreferrer"
                style={{ display: "inline-flex", alignItems: "center", gap: 8, padding: "11px 18px", background: "#F5F1E8", color: "#2F4A3A", borderRadius: 999, fontSize: 14, fontWeight: 600, textDecoration: "none" }}
              >
                <Star size={14} fill="#2F4A3A" strokeWidth={0} /> Leave a Google review
              </a>
            )}
            {r.zillow_url && (
              <a
                data-testid="referral-zillow-btn"
                href={r.zillow_url}
                target="_blank"
                rel="noopener noreferrer"
                style={{ display: "inline-flex", alignItems: "center", gap: 8, padding: "11px 18px", background: "rgba(255,255,255,0.14)", color: "#F5F1E8", borderRadius: 999, fontSize: 14, fontWeight: 600, textDecoration: "none", border: "1px solid rgba(245,241,232,0.3)" }}
              >
                <Star size={14} fill="#F5F1E8" strokeWidth={0} /> Review on Zillow
              </a>
            )}
          </div>
        </div>
      </div>
    </div>
  );
}


function UnderContractCard({ p, pinned = false }) {
  const { content } = useContent();
  // Per-seller override wins over agent-wide CMS settings (Phase 2B.3.7).
  const ucFields = (p.under_contract_fields_override && p.under_contract_fields_override.length > 0)
    ? p.under_contract_fields_override
    : (content?.under_contract_fields || []);
  const uc = p.under_contract || {};
  if (!uc.active) return null;
  const items = ucFields
    .map((f) => ({ ...f, ...(uc[f.key] || {}) }))
    .filter((it) => it.date || it.description);
  if (items.length === 0) return null;

  return (
    <div style={{ marginTop: pinned ? 32 : 48 }} data-testid="under-contract-card">
      <span className="overline" style={{ color: "var(--secondary)" }}>Under contract</span>
      <h2 className="font-serif" style={{ fontSize: "clamp(1.75rem, 3vw, 2.25rem)", fontWeight: 500, letterSpacing: "-0.01em", marginTop: 8, display: "inline-flex", alignItems: "center", gap: 12 }}>
        <FileSignature size={28} style={{ color: "var(--secondary)" }} /> Contract milestones
      </h2>
      <p style={{ color: "var(--text-muted)", marginTop: 6, maxWidth: 640 }}>
        Important dates from your agent for this contract phase. Reach out anytime if a date or description needs clarifying.
      </p>
      <div style={{ display: "grid", gridTemplateColumns: "repeat(auto-fit, minmax(260px, 1fr))", gap: 16, marginTop: 20 }}>
        {items.map((it) => {
          let when = "";
          let countdown = "";
          if (it.date) {
            const d = new Date(it.date + "T00:00:00");
            when = d.toLocaleDateString(undefined, { weekday: "short", month: "long", day: "numeric" });
            if (it.hasTime && it.time) {
              const raw = String(it.time).trim();
              const strict = /^\d{1,2}:\d{2}(:\d{2})?$/.test(raw);
              const [hh, mm] = (strict ? raw : "00:00").split(":");
              const hr = parseInt(hh, 10);
              const ampm = hr >= 12 ? "PM" : "AM";
              const h12 = ((hr + 11) % 12) + 1;
              const shown = strict ? h12 + ":" + mm + " " + ampm : raw;
              when += " · " + shown;
            }
            const now = new Date(); now.setHours(0, 0, 0, 0);
            const diffDays = Math.round((d - now) / 86400000);
            countdown = diffDays > 0 ? `in ${diffDays} day${diffDays === 1 ? "" : "s"}` : diffDays === 0 ? "today" : `${-diffDays} day${diffDays === -1 ? "" : "s"} ago`;
          }
          return (
            <div key={it.key} className="card" style={{ padding: 22, borderLeft: "3px solid var(--secondary)" }} data-testid={`uc-card-${it.key}`}>
              <div style={{ display: "flex", alignItems: "center", gap: 8, color: "var(--secondary)" }}>
                <Calendar size={14} />
                <span className="overline" style={{ color: "var(--secondary)" }}>{it.label}</span>
              </div>
              {when ? (
                <>
                  <div className="font-serif" style={{ fontSize: 20, marginTop: 8 }}>{when}</div>
                  {countdown && <div style={{ color: "var(--text-muted)", fontSize: 13, marginTop: 2 }}>{countdown}</div>}
                </>
              ) : (
                <div style={{ color: "var(--text-muted)", fontSize: 14, marginTop: 8, fontStyle: "italic" }}>Date coming soon</div>
              )}
              {it.description && (
                <p style={{ marginTop: 10, color: "var(--text)", fontSize: 14, lineHeight: 1.55 }}>{it.description}</p>
              )}
            </div>
          );
        })}
      </div>
    </div>
  );
}
